import { useEffect, useState } from "react";
import API from "../../services/api";
import Navbar from "../../components/Navbar";

type FilterType = "today" | "tomorrow" | "rest" | "all";

interface PendingData {
  type?: string;
  filter?: string;
  date?: string;
  date_range?: string;
  summary?: {
    total_users?: number;
    total_items?: number;
    total_amount?: number;
  };
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    pages?: number;
    has_next?: boolean;
    has_previous?: boolean;
  };
  [key: string]: any;
}

export default function PendingSubscribedCartItems() {
  const [type, setType] = useState<FilterType>("tomorrow");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PendingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const fetchItems = async (selectedType: FilterType, pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.get(
        `/pending-subscribed-cart-items?type=${selectedType}&page=${pageNum}&limit=10`
      );
      if (response.data?.success) {
        setData(response.data.data);
        const rows = getRows(response.data.data);
        if (rows.length) setExpandedRows(new Set([getRowId(rows[0], 0)]));
      } else {
        setError(response.data?.message || "Failed to fetch pending subscribed cart items");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch pending subscribed cart items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(type, page);
  }, [type, page]);

  const getRows = (source: PendingData | null): any[] => {
    if (!source) return [];
    for (const key of ["users", "orders", "subscriptions", "items", "cart_items", "pending_items", "results", "data"]) {
      if (Array.isArray(source[key])) return source[key];
    }
    return [];
  };

  const getRowId = (row: any, index: number) =>
    String(row.user_id || row._id || row.id || row.subscription_id || index);

  const getItems = (row: any): any[] => {
    for (const key of ["items", "cart_items", "pending_items", "products"]) {
      if (Array.isArray(row?.[key])) return row[key];
    }
    return [];
  };

  const toggleRow = (id: string) => {
    setExpandedRows((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setType(e.target.value as FilterType);
    setPage(1);
  };

  const rows = getRows(data);
  const pages = data?.pagination?.pages || 0;

  const getDeliveryDate = (item: any, row: any) =>
    item?.delivery_date || item?.deliveryDate || item?.date ||
    row?.delivery_date || row?.deliveryDate || row?.date ||
    data?.date || "Date not available";

  const dateGroups = (() => {
    const groups: Record<string, any[]> = {};

    rows.forEach((row: any) => {
      const items = getItems(row);

      if (!items.length) {
        const date = getDeliveryDate(null, row);
        groups[date] = groups[date] || [];
        groups[date].push(row);
        return;
      }

      const itemsByDate: Record<string, any[]> = {};
      items.forEach((item: any) => {
        const date = getDeliveryDate(item, row);
        itemsByDate[date] = itemsByDate[date] || [];
        itemsByDate[date].push(item);
      });

      Object.entries(itemsByDate).forEach(([date, dateItems]) => {
        groups[date] = groups[date] || [];
        groups[date].push({ ...row, items: dateItems });
      });
    });

    return Object.entries(groups).sort(([dateA], [dateB]) => {
      const a = Date.parse(dateA);
      const b = Date.parse(dateB);
      if (Number.isNaN(a) || Number.isNaN(b)) return dateA.localeCompare(dateB);
      return a - b;
    });
  })();

  const formatDisplayDate = (date: string) => {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Pending Subscribed Cart Items</h1>
          <p style={styles.subtitle}>Subscribed cart items grouped for easy processing</p>
        </div>

        <div style={styles.controls}>
          <div style={styles.filterGroup}>
            <label style={styles.label}>Filter Items:</label>
            <select value={type} onChange={handleFilterChange} style={styles.select}>
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="rest">Rest</option>
              <option value="all">All</option>
            </select>
          </div>

          {data && pages > 0 && (
            <div style={styles.pageInfo}>Page {data.pagination?.page || page} of {pages}</div>
          )}
        </div>

        {loading && (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Loading subscribed cart items...</p>
          </div>
        )}

        {error && (
          <div style={styles.error}>
            <p>{error}</p>
            <button onClick={() => fetchItems(type, page)} style={styles.retryButton}>Retry</button>
          </div>
        )}

        {data && !loading && !error && (
          <>
            <div style={styles.summaryGrid}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>👥</div>
                <div>
                  <div style={styles.summaryLabel}>Total Customers</div>
                  <div style={styles.summaryValue}>{data.summary?.total_users ?? 0}</div>
                </div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>🛒</div>
                <div>
                  <div style={styles.summaryLabel}>Total Items</div>
                  <div style={styles.summaryValue}>{data.summary?.total_items ?? 0}</div>
                </div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>💰</div>
                <div>
                  <div style={styles.summaryLabel}>Total Amount</div>
                  <div style={styles.summaryValue}>₹{data.summary?.total_amount ?? 0}</div>
                </div>
              </div>
            </div>

            {rows.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>📭</div>
                <h3>No pending subscribed cart items found</h3>
                <p>No items available for the selected filter</p>
              </div>
            ) : (
              <div style={styles.dateGroups}>
                {dateGroups.map(([deliveryDate, dateRows]) => (
                  <div key={deliveryDate} style={styles.dateSection}>
                    <div style={styles.dateHeader}>
                      <span style={styles.dateIcon}>📅</span>
                      <span>{formatDisplayDate(deliveryDate)}</span>
                      <span style={styles.dateCount}>
                        {dateRows.reduce((sum: number, row: any) => sum + getItems(row).reduce((itemSum: number, item: any) => itemSum + Number(item.quantity || 1), 0), 0)} items
                      </span>
                    </div>
                    <div style={styles.userOrdersList}>
                {dateRows.map((row: any, index: number) => {
                  const id = `${deliveryDate}-${getRowId(row, index)}`;
                  const isExpanded = expandedRows.has(id);
                  const items = getItems(row);
                  const userName = row.user_name || row.full_name || row.name || "Customer";
                  const email = row.email || row.user_email;
                  const phone = row.phone || row.phone_number || row.user_phone;
                  const address = row.address || row.user_address;
                  const totalItems = row.total_items ?? items.reduce((sum: number, item: any) => sum + Number(item.quantity || 1), 0);
                  const totalAmount = row.total_amount ?? items.reduce((sum: number, item: any) => sum + Number(item.total_price || item.amount || item.price || 0), 0);

                  return (
                    <div key={id} style={styles.userCard}>
                      <div style={styles.userHeader} onClick={() => toggleRow(id)}>
                        <div style={styles.userInfo}>
                          <div>
                            <div style={styles.userName}>{userName}</div>
                            <div style={styles.userContact}>
                              {email && <span>{email}</span>}
                              {phone && <span>• {phone}</span>}
                            </div>
                            {address && <div style={styles.userAddress}>📍 {address}</div>}
                          </div>
                        </div>
                        <div style={styles.userStats}>
                          <span style={styles.statBadge}>🛒 {totalItems} Items</span>
                          <span style={styles.statBadge}>₹{totalAmount}</span>
                          <span style={styles.expandIcon}>{isExpanded ? "▼" : "▶"}</span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={styles.itemsContainer}>
                          <div style={styles.itemsHeader}>
                            <span style={styles.itemsTitle}>Subscribed Cart Items</span>
                            <span style={styles.typeBadge}>Subscription</span>
                          </div>
                          <div style={styles.itemsGrid}>
                            {items.length ? items.map((item: any, itemIndex: number) => (
                              <div key={item._id || item.id || itemIndex} style={styles.itemRow}>
                                {item.image && (
                                  <div style={styles.itemImageContainer}>
                                    <img src={item.image} alt={item.product_name || item.name || "Product"} style={styles.itemImage} />
                                  </div>
                                )}
                                <div style={styles.itemDetails}>
                                  <div style={styles.itemName}>{item.product_name || item.name || item.product?.name || "Product"}</div>
                                  <div style={styles.itemMeta}>
                                    <span>Qty: {item.quantity ?? 1}</span>
                                    {(item.price !== undefined) && <><span>•</span><span>₹{item.price} each</span></>}
                                    {(item.delivery_date || item.date) && <><span>•</span><span style={styles.deliveryDate}>📅 {item.delivery_date || item.date}</span></>}
                                  </div>
                                </div>
                                <div style={styles.itemTotal}>
                                  <span style={styles.itemTotalLabel}>Total</span>
                                  <span style={styles.itemTotalAmount}>₹{item.total_price ?? item.amount ?? item.price ?? 0}</span>
                                </div>
                              </div>
                            )) : (
                              <div style={styles.rawDetails}>
                                {Object.entries(row)
                                  .filter(([key]) => !["items", "cart_items", "pending_items", "products"].includes(key))
                                  .map(([key, value]) => (
                                    <div key={key}><strong>{key.replace(/_/g, " ")}:</strong> {formatValue(value)}</div>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {rows.length > 0 && pages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
                >← Previous</button>
                <span style={styles.pageInfo}>Page {page} of {pages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  style={{ ...styles.pageButton, ...(page === pages ? styles.pageButtonDisabled : {}) }}
                >Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "24px", maxWidth: "1400px", margin: "0 auto", backgroundColor: "#f5f7fa", minHeight: "100vh" },
  header: { marginBottom: "24px" },
  title: { fontSize: "28px", fontWeight: "700", color: "#1a2332", margin: 0 },
  subtitle: { fontSize: "14px", color: "#666", marginTop: "4px" },
  controls: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "16px" },
  filterGroup: { display: "flex", alignItems: "center", gap: "12px", backgroundColor: "#fff", padding: "8px 16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  label: { fontSize: "14px", fontWeight: "600", color: "#555" },
  select: { padding: "8px 12px", borderRadius: "6px", border: "1px solid #ddd", fontSize: "14px", backgroundColor: "#fff", cursor: "pointer", outline: "none", minWidth: "150px" },
  pageInfo: { fontSize: "14px", color: "#666", backgroundColor: "#fff", padding: "8px 16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  loading: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", color: "#666" },
  spinner: { width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #3498db", borderRadius: "50%", animation: "spin 1s linear infinite", marginBottom: "16px" },
  error: { backgroundColor: "#fff3f3", border: "1px solid #f44336", borderRadius: "8px", padding: "16px 20px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#d32f2f" },
  retryButton: { padding: "8px 20px", backgroundColor: "#f44336", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "600" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" },
  summaryCard: { backgroundColor: "#fff", padding: "16px 20px", borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.08)", display: "flex", alignItems: "center", gap: "12px", border: "1px solid #e8ecf1" },
  summaryIcon: { fontSize: "28px" },
  summaryLabel: { fontSize: "12px", color: "#888", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" },
  summaryValue: { fontSize: "20px", fontWeight: "700", color: "#1a2332" },
  emptyState: { textAlign: "center", padding: "60px 20px", backgroundColor: "#fff", borderRadius: "10px", border: "1px dashed #ddd" },
  emptyIcon: { fontSize: "48px", marginBottom: "16px" },
  dateGroups: { display: "flex", flexDirection: "column", gap: "24px" },
  dateSection: { display: "flex", flexDirection: "column", gap: "12px" },
  dateHeader: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", backgroundColor: "#e3f2fd", color: "#1565c0", borderRadius: "8px", fontSize: "16px", fontWeight: "700", border: "1px solid #bbdefb" },
  dateIcon: { fontSize: "18px" },
  dateCount: { marginLeft: "auto", padding: "3px 10px", backgroundColor: "#fff", borderRadius: "20px", fontSize: "12px", fontWeight: "600", color: "#555" },
  userOrdersList: { display: "flex", flexDirection: "column", gap: "16px" },
  userCard: { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", border: "1px solid #e8ecf1", overflow: "hidden" },
  userHeader: { padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", backgroundColor: "#fafbfc", borderBottom: "1px solid #e8ecf1", flexWrap: "wrap", gap: "12px" },
  userInfo: { display: "flex", alignItems: "center", gap: "12px" },
  userName: { fontSize: "16px", fontWeight: "600", color: "#1a2332" },
  userContact: { fontSize: "13px", color: "#666", display: "flex", gap: "8px", flexWrap: "wrap" },
  userAddress: { fontSize: "12px", color: "#888", marginTop: "3px" },
  userStats: { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" },
  statBadge: { padding: "4px 12px", backgroundColor: "#f0f2f5", borderRadius: "20px", fontSize: "13px", fontWeight: "500", color: "#333" },
  expandIcon: { fontSize: "18px", color: "#666", marginLeft: "4px" },
  itemsContainer: { padding: "16px 20px", backgroundColor: "#fafbfc" },
  itemsHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" },
  itemsTitle: { fontSize: "14px", fontWeight: "600", color: "#555" },
  typeBadge: { padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "600", backgroundColor: "#e3f2fd", color: "#1976d2" },
  itemsGrid: { display: "flex", flexDirection: "column", gap: "8px" },
  itemRow: { display: "flex", alignItems: "center", gap: "16px", padding: "12px", backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #e8ecf1", flexWrap: "wrap" },
  itemImageContainer: { width: "60px", height: "60px", flexShrink: 0 },
  itemImage: { width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px", backgroundColor: "#f5f5f5" },
  itemDetails: { flex: 1, minWidth: "150px" },
  itemName: { fontSize: "14px", fontWeight: "500", color: "#1a2332", marginBottom: "4px" },
  itemMeta: { fontSize: "12px", color: "#888", display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" },
  deliveryDate: { color: "#2196f3", fontWeight: "500" },
  itemTotal: { display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: "80px" },
  itemTotalLabel: { fontSize: "11px", color: "#888", textTransform: "uppercase" },
  itemTotalAmount: { fontSize: "16px", fontWeight: "700", color: "#2196f3" },
  rawDetails: { padding: "12px", backgroundColor: "#fff", border: "1px solid #e8ecf1", borderRadius: "8px", fontSize: "13px", lineHeight: "1.7" },
  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginTop: "24px", padding: "16px 0", flexWrap: "wrap" },
  pageButton: { padding: "8px 16px", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "500", color: "#333" },
  pageButtonDisabled: { opacity: 0.5, cursor: "not-allowed" },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
